import type { MutableRefObject } from 'react';

interface UsePermissionTestersParams {
  streamRef: MutableRefObject<MediaStream | null>;
  cameraPermissionStatusRef: MutableRefObject<PermissionStatus | null>;
  micPermissionStatusRef: MutableRefObject<PermissionStatus | null>;
  setError: (error: string | null) => void;
  setCameraDismissed: (dismissed: boolean) => void;
  setCameraGranted: (granted: boolean) => void;
  setIsTestingCamera: (testing: boolean) => void;
  setCameraPermissionState: (state: string) => void;
  setMicDismissed: (dismissed: boolean) => void;
  setMicGranted: (granted: boolean) => void;
  setIsTestingMic: (testing: boolean) => void;
  setMicPermissionState: (state: string) => void;
}

export default function usePermissionTesters({
  streamRef,
  cameraPermissionStatusRef,
  micPermissionStatusRef,
  setError,
  setCameraDismissed,
  setCameraGranted,
  setIsTestingCamera,
  setCameraPermissionState,
  setMicDismissed,
  setMicGranted,
  setIsTestingMic,
  setMicPermissionState
}: UsePermissionTestersParams) {
  // Test camera
  const testCamera = async () => {
    setError(null);
    setCameraDismissed(false);
    setCameraGranted(false); // Reset granted state to allow re-requesting
    setIsTestingCamera(true);

    // Clean up any existing stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      // eslint-disable-next-line no-param-reassign
      streamRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });

      // eslint-disable-next-line no-param-reassign
      streamRef.current = stream;
      setCameraGranted(true);
      setCameraPermissionState('granted');
      setIsTestingCamera(false);
    } catch (err: any) {
      // Check if permission is now permanently blocked after the error
      let isPermanentlyBlocked = false;
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'camera' as any
        });
        isPermanentlyBlocked = permissionStatus.state === 'denied';
        // Keep our tracked status/state in sync
        // eslint-disable-next-line no-param-reassign
        cameraPermissionStatusRef.current = permissionStatus;
        setCameraPermissionState(permissionStatus.state);
      } catch (e) {
        // Permissions API not supported
      }

      if (err.name === 'NotAllowedError') {
        if (isPermanentlyBlocked) {
          // User clicked "Block" or "Never Allow" - image swap handles this,
          // no need for the old text block.
          setCameraDismissed(false);
          setError(null);
        } else {
          // User closed the native dialog with the X (didn't explicitly
          // block) - permission state is still 'prompt'.
          setCameraDismissed(true);
          setError(null);
        }
      } else if (err.name === 'NotFoundError') {
        setCameraDismissed(false);
        setError(
          '📷 No camera found.\n\nPlease connect a camera device and try again.'
        );
      } else if (err.name === 'NotReadableError') {
        setCameraDismissed(false);
        setError(
          '⚠️ Camera is already in use.\n\nPlease close other apps using your camera and try again.'
        );
      } else {
        setCameraDismissed(false);
        setError(`Camera error: ${err.message}`);
      }

      setCameraGranted(false);
      setIsTestingCamera(false);
    }
  };

  // Test microphone
  const testMicrophone = async () => {
    setError(null);
    setMicDismissed(false);
    setMicGranted(false); // Reset granted state to allow re-requesting
    setIsTestingMic(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      // Combine audio stream with existing video stream
      if (streamRef.current) {
        // Remove old audio tracks first
        streamRef.current.getAudioTracks().forEach((track) => {
          streamRef.current.removeTrack(track);
          track.stop();
        });
        // Add new audio track
        stream.getAudioTracks().forEach((track) => {
          streamRef.current.addTrack(track);
        });
      }

      setMicGranted(true);
      setMicPermissionState('granted');
      setIsTestingMic(false);
    } catch (err: any) {
      // Check if permission is now permanently blocked after the error
      let isPermanentlyBlocked = false;
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as any
        });
        isPermanentlyBlocked = permissionStatus.state === 'denied';
        // Keep our tracked status/state in sync
        // eslint-disable-next-line no-param-reassign
        micPermissionStatusRef.current = permissionStatus;
        setMicPermissionState(permissionStatus.state);
      } catch (e) {
        // Permissions API not supported
      }

      if (err.name === 'NotAllowedError') {
        if (isPermanentlyBlocked) {
          // User clicked "Block" or "Never Allow" - image swap handles this,
          // no need for the old text block.
          setMicDismissed(false);
          setError(null);
        } else {
          // User closed the native dialog with the X (didn't explicitly
          // block) - permission state is still 'prompt'.
          setMicDismissed(true);
          setError(null);
        }
      } else if (err.name === 'NotFoundError') {
        setMicDismissed(false);
        setError(
          '🎤 No microphone found.\n\nPlease connect a microphone device and try again.'
        );
      } else if (err.name === 'NotReadableError') {
        setMicDismissed(false);
        setError(
          '⚠️ Microphone is already in use.\n\nPlease close other apps using your microphone and try again.'
        );
      } else {
        setMicDismissed(false);
        setError(`Microphone error: ${err.message}`);
      }

      setMicGranted(false);
      setIsTestingMic(false);
    }
  };

  return { testCamera, testMicrophone };
}
